import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash-es';
import { useStateInRef } from 'seagull';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { useAppConfig } from '~/store/store-context';
export const lastUserInteractionKey = 'lastUserInteraction';
const defaultUserInputDebounceTime = 500;
const uiEvents = [
    'click',
    'keydown'
];
export function useSessionTimeout(disableOnMount) {
    const getDisableOnMount = useStateInRef(disableOnMount);
    const timeoutRef = useRef();
    const didMount = useRef(false);
    const navigate = useNavigate();
    const { localStorage, getNow, browser: { locationReload, registerBecameVisibleListener } } = useIoContext();
    const { reset } = useStoreActions();
    const { getState } = useStoreStatic();
    const isLoggedIn = useStore((state)=>!!state.userInfo && state.authFlowDone);
    const { inputDebounceTime } = useAppConfig();
    useEffect(()=>{
        const { sessionTimeoutMS } = getState().session;
        let sessionEnded = false;
        function endSession() {
            if (sessionEnded) {
                return;
            }
            sessionEnded = true;
            stopSessionTimeout();
            reset();
            const state = {
                sessionTimeout: true
            };
            navigate(routes.home, {
                state
            });
            setTimeout(()=>locationReload());
        }
        function armSessionTimeout(delayMS) {
            timeoutRef.current && clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(()=>{
                if (!isLoggedIn) return;
                endSession();
            }, delayMS);
        }
        function initSessionTimeout(updateLocalStorage) {
            if (!isLoggedIn) {
                return;
            }
            if (updateLocalStorage) {
                localStorage.setItem(lastUserInteractionKey, getNow().getTime().toString());
            }
            armSessionTimeout(sessionTimeoutMS);
        }
        function readLastUserInteraction() {
            const storedValue = Number(localStorage.getItem(lastUserInteractionKey));
            return Number.isFinite(storedValue) ? storedValue : null;
        }
        function reconcileSessionTimeout() {
            if (!isLoggedIn) {
                return;
            }
            const lastUserInteraction = readLastUserInteraction();
            if (lastUserInteraction == null) {
                endSession();
                return;
            }
            const idleMS = getNow().getTime() - lastUserInteraction;
            if (idleMS >= sessionTimeoutMS || idleMS <= -sessionTimeoutMS) {
                endSession();
                return;
            }
            armSessionTimeout(sessionTimeoutMS - Math.max(0, idleMS));
        }
        function stopSessionTimeout() {
            timeoutRef.current && clearTimeout(timeoutRef.current);
        }
        const debouncedInitSessionTimeout = debounce(initSessionTimeout, inputDebounceTime != null ? inputDebounceTime : defaultUserInputDebounceTime);
        function onInteractionRecordedInAnotherTab(event) {
            if (event.key === lastUserInteractionKey) {
                initSessionTimeout(false);
            }
        }
        function onUserInteraction() {
            debouncedInitSessionTimeout(true);
        }
        window.addEventListener('storage', onInteractionRecordedInAnotherTab);
        uiEvents.forEach((event)=>window.addEventListener(event, onUserInteraction));
        const unregisterBecameVisibleListener = registerBecameVisibleListener(reconcileSessionTimeout);
        if (isLoggedIn && (didMount.current || !getDisableOnMount())) {
            initSessionTimeout(true);
        }
        didMount.current = true;
        return ()=>{
            window.removeEventListener('storage', onInteractionRecordedInAnotherTab);
            uiEvents.forEach((event)=>window.removeEventListener(event, onUserInteraction));
            unregisterBecameVisibleListener();
            stopSessionTimeout();
            debouncedInitSessionTimeout.cancel();
        };
    }, [
        isLoggedIn
    ]);
}
